import { collection, addDoc, query, where, getDocs, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Message } from '../types/messages';
import { Task } from '../types/tasks';

export interface AISummary {
  id: string;
  type: 'message' | 'task' | 'attendance' | 'general';
  sourceIds: string[];
  summary: string;
  keyPoints: string[];
  actionItems?: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
  priority?: 'low' | 'medium' | 'high';
  createdAt: string;
  schoolId: string;
  createdBy: string;
}

export interface AITaskSuggestion {
  id: string;
  taskTitle: string;
  description: string;
  suggestedAssignees: string[];
  suggestedPriority: 'low' | 'medium' | 'high';
  suggestedDueDate: string;
  reasoning: string;
  confidence: number;
  sourceData: any;
  schoolId: string;
  createdAt: string;
  status: 'pending' | 'accepted' | 'rejected';
}

export interface AIInsight {
  id: string;
  type: 'pattern' | 'trend' | 'anomaly' | 'recommendation';
  title: string;
  description: string;
  data: any;
  actionable: boolean;
  priority: 'low' | 'medium' | 'high';
  schoolId: string;
  createdAt: string;
}

export class AIService {
  private static readonly GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || import.meta.env.VITE_OPENAI_API_KEY;
  private static readonly API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent';

  // Message Summarization
  static async summarizeMessages(
    messages: Message[],
    schoolId: string,
    userId: string,
    timeframe?: string
  ): Promise<AISummary> {
    console.log('[AIService] summarizeMessages called:', {
      messageCount: messages.length,
      schoolId,
      userId,
      timeframe,
      hasApiKey: !!this.GEMINI_API_KEY
    });

    if (!this.GEMINI_API_KEY) {
      console.error('[AIService] Gemini API key missing');
      throw new Error('AI API key not configured');
    }

    try {
      // Prepare messages for summarization
      const messageTexts = messages.map(msg => ({
        sender: msg.senderId,
        content: msg.content,
        timestamp: msg.createdAt
      }));

      console.log('[AIService] Prepared messages:', messageTexts.length);

      const prompt = this.buildMessageSummaryPrompt(messageTexts, timeframe);
      console.log('[AIService] Generated prompt');

      const response = await this.callGemini(prompt);
      console.log('[AIService] Gemini response received');

      const summaryData = JSON.parse(response);
      console.log('[AIService] Parsed summary data:', summaryData);

      // Create summary object
      const summary: AISummary = {
        id: `temp-${Date.now()}`,
        type: 'message',
        sourceIds: messages.map(m => m.id),
        summary: summaryData.summary,
        keyPoints: summaryData.keyPoints || [],
        actionItems: summaryData.actionItems || [],
        sentiment: summaryData.sentiment || 'neutral',
        priority: summaryData.priority || 'medium',
        createdAt: new Date().toISOString(),
        schoolId,
        createdBy: userId
      };

      console.log('[AIService] Returning summary:', summary);
      return summary;
    } catch (error: any) {
      console.error('Error summarizing messages:', error);
      throw new Error(`Failed to summarize messages: ${error.message}`);
    }
  }

  // Smart Task Suggestions
  static async generateTaskSuggestions(
    context: {
      messages?: Message[];
      attendance?: any[];
      grades?: any[];
      events?: any[];
    },
    schoolId: string
  ): Promise<AITaskSuggestion[]> {
    if (!this.GEMINI_API_KEY) {
      throw new Error('AI API key not configured');
    }

    try {
      const prompt = this.buildTaskSuggestionPrompt(context);
      const response = await this.callGemini(prompt);

      const suggestions = JSON.parse(response);
      const taskSuggestions: AITaskSuggestion[] = [];

      for (const suggestion of suggestions) {
        const taskSuggestion: Omit<AITaskSuggestion, 'id'> = {
          taskTitle: suggestion.title,
          description: suggestion.description,
          suggestedAssignees: suggestion.assignees || [],
          suggestedPriority: suggestion.priority || 'medium',
          suggestedDueDate: suggestion.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          reasoning: suggestion.reasoning,
          confidence: suggestion.confidence || 0.7,
          sourceData: context,
          schoolId,
          createdAt: new Date().toISOString(),
          status: 'pending'
        };

        const docRef = await addDoc(collection(db, 'aiTaskSuggestions'), {
          ...taskSuggestion,
          createdAt: serverTimestamp()
        });

        taskSuggestions.push({ id: docRef.id, ...taskSuggestion });
      }

      return taskSuggestions;
    } catch (error: any) {
      console.error('Error generating task suggestions:', error);
      throw new Error(`Failed to generate task suggestions: ${error.message}`);
    }
  }

  // Content Analysis
  static async analyzeMessageContent(content: string): Promise<{
    sentiment: 'positive' | 'neutral' | 'negative';
    urgency: 'low' | 'medium' | 'high';
    topics: string[];
    actionItems: string[];
  }> {
    if (!this.GEMINI_API_KEY) {
      return {
        sentiment: 'neutral',
        urgency: 'medium',
        topics: [],
        actionItems: []
      };
    }

    try {
      const prompt = `
        Analyze this message content and return a JSON response with:
        - sentiment: positive, neutral, or negative
        - urgency: low, medium, or high
        - topics: array of main topics/themes
        - actionItems: array of any action items mentioned

        Message: "${content}"

        Return only valid JSON.
      `;

      const response = await this.callGemini(prompt);
      return JSON.parse(response);
    } catch (error) {
      console.warn('Error analyzing message content:', error);
      return {
        sentiment: 'neutral',
        urgency: 'medium',
        topics: [],
        actionItems: []
      };
    }
  }

  // Intelligent Assignment Suggestions
  static async suggestTaskAssignees(
    taskDescription: string,
    availableUsers: Array<{ id: string; name: string; role: string; department?: string; skills?: string[] }>,
    schoolId: string
  ): Promise<{
    suggestions: Array<{
      userId: string;
      name: string;
      confidence: number;
      reasoning: string;
    }>;
  }> {
    if (!this.GEMINI_API_KEY) {
      // Fallback to simple logic
      return {
        suggestions: availableUsers.slice(0, 3).map(user => ({
          userId: user.id,
          name: user.name,
          confidence: 0.5,
          reasoning: 'Random assignment (AI not available)'
        }))
      };
    }

    try {
      const prompt = `
        Given this task description: "${taskDescription}"

        And these available users:
        ${availableUsers.map(user => `- ${user.name} (${user.role}${user.department ? `, ${user.department}` : ''}${user.skills ? `, Skills: ${user.skills.join(', ')}` : ''})`).join('\n')}

        Suggest the best 3 users to assign this task to. Return JSON with:
        {
          "suggestions": [
            {
              "userId": "user_id",
              "name": "User Name",
              "confidence": 0.8,
              "reasoning": "Why this person is a good fit"
            }
          ]
        }

        Consider role relevance, department fit, and skills match.
      `;

      const response = await this.callGemini(prompt);
      return JSON.parse(response);
    } catch (error) {
      console.error('Error suggesting task assignees:', error);
      return {
        suggestions: availableUsers.slice(0, 3).map(user => ({
          userId: user.id,
          name: user.name,
          confidence: 0.5,
          reasoning: 'Fallback assignment'
        }))
      };
    }
  }

  // Generate Insights from Data
  static async generateInsights(
    data: {
      attendance?: any[];
      grades?: any[];
      messages?: Message[];
      tasks?: Task[];
    },
    schoolId: string
  ): Promise<AIInsight[]> {
    if (!this.GEMINI_API_KEY) {
      return [];
    }

    try {
      const prompt = this.buildInsightsPrompt(data);
      const response = await this.callGemini(prompt);

      const insights = JSON.parse(response);
      const aiInsights: AIInsight[] = [];

      for (const insight of insights) {
        const aiInsight: Omit<AIInsight, 'id'> = {
          type: insight.type || 'pattern',
          title: insight.title,
          description: insight.description,
          data: insight.data || {},
          actionable: insight.actionable || false,
          priority: insight.priority || 'medium',
          schoolId,
          createdAt: new Date().toISOString()
        };

        const docRef = await addDoc(collection(db, 'aiInsights'), {
          ...aiInsight,
          createdAt: serverTimestamp()
        });

        aiInsights.push({ id: docRef.id, ...aiInsight });
      }

      return aiInsights;
    } catch (error) {
      console.error('Error generating insights:', error);
      return [];
    }
  }

  // Smart Reply Suggestions
  static async generateReplySuggestions(
    messageContent: string,
    context?: string
  ): Promise<string[]> {
    if (!this.GEMINI_API_KEY) {
      return [
        "Thank you for the update.",
        "I'll look into this.",
        "Let me get back to you on this."
      ];
    }

    try {
      const prompt = `
        Generate 3 professional, contextually appropriate reply suggestions for this message:
        "${messageContent}"

        ${context ? `Context: ${context}` : ''}

        Return as JSON array of strings. Keep replies concise and professional.
      `;

      const response = await this.callGemini(prompt);
      return JSON.parse(response);
    } catch (error) {
      console.error('Error generating reply suggestions:', error);
      return [
        "Thank you for the update.",
        "I'll look into this.",
        "Let me get back to you on this."
      ];
    }
  }

  // Private helper methods
  private static async callGemini(prompt: string): Promise<string> {
    const url = `${this.API_URL}?key=${this.GEMINI_API_KEY}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              {
                text: `You are an AI assistant specialized in educational administration. Always return valid JSON when requested.\n\n${prompt}`
              }
            ]
          }
        ],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 2000,
          responseMimeType: 'application/json'
        }
      })
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('[AIService] Gemini API error:', response.status, errorBody);
      throw new Error(`AI API error: ${response.statusText}`);
    }

    const data = await response.json();

    // Gemini response structure: candidates[0].content.parts[0].text
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!text) {
      throw new Error('Empty response from AI API');
    }

    return text;
  }

  private static buildMessageSummaryPrompt(messages: any[], timeframe?: string): string {
    return `
      Summarize these school communication messages from ${timeframe || 'recent conversations'}.

      Messages:
      ${messages.map((msg, i) => `${i + 1}. [${msg.timestamp}] ${msg.sender}: ${msg.content}`).join('\n')}

      Provide a JSON response with:
      {
        "summary": "Brief 2-3 sentence summary",
        "keyPoints": ["key point 1", "key point 2", "..."],
        "actionItems": ["action 1", "action 2", "..."],
        "sentiment": "positive|neutral|negative",
        "priority": "low|medium|high"
      }

      Focus on educational administration context.
    `;
  }

  private static buildTaskSuggestionPrompt(context: any): string {
    return `
      Based on this school data, suggest important tasks that should be created:

      ${context.messages ? `Recent Messages: ${context.messages.length} messages` : ''}
      ${context.attendance ? `Attendance Issues: ${context.attendance.length} records` : ''}
      ${context.grades ? `Grade Issues: ${context.grades.length} records` : ''}
      ${context.events ? `Upcoming Events: ${context.events.length} events` : ''}

      Return JSON array of task suggestions:
      [
        {
          "title": "Task title",
          "description": "Detailed description",
          "assignees": ["role1", "role2"],
          "priority": "low|medium|high",
          "dueDate": "ISO date string",
          "reasoning": "Why this task is important",
          "confidence": 0.8
        }
      ]

      Focus on actionable, school-specific tasks.
    `;
  }

  private static buildInsightsPrompt(data: any): string {
    return `
      Analyze this school data and provide insights:

      Data Summary:
      - Attendance records: ${data.attendance?.length || 0}
      - Grade records: ${data.grades?.length || 0}
      - Messages: ${data.messages?.length || 0}
      - Tasks: ${data.tasks?.length || 0}

      Return JSON array of insights:
      [
        {
          "type": "pattern|trend|anomaly|recommendation",
          "title": "Insight title",
          "description": "Detailed description",
          "data": {},
          "actionable": true,
          "priority": "low|medium|high"
        }
      ]

      Focus on educational trends, patterns, and actionable recommendations.
    `;
  }

  // Utility methods
  static async getSummaries(schoolId: string, type?: AISummary['type']): Promise<AISummary[]> {
    try {
      let q = query(
        collection(db, 'aiSummaries'),
        where('schoolId', '==', schoolId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      if (type) {
        q = query(q, where('type', '==', type));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AISummary[];
    } catch (error) {
      console.error('Error getting summaries:', error);
      return [];
    }
  }

  static async getTaskSuggestions(schoolId: string, status?: AITaskSuggestion['status']): Promise<AITaskSuggestion[]> {
    try {
      let q = query(
        collection(db, 'aiTaskSuggestions'),
        where('schoolId', '==', schoolId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      if (status) {
        q = query(q, where('status', '==', status));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AITaskSuggestion[];
    } catch (error) {
      console.error('Error getting task suggestions:', error);
      return [];
    }
  }

  static async getInsights(schoolId: string): Promise<AIInsight[]> {
    try {
      const q = query(
        collection(db, 'aiInsights'),
        where('schoolId', '==', schoolId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AIInsight[];
    } catch (error) {
      console.error('Error getting insights:', error);
      return [];
    }
  }
}

export default AIService;
